/**
 * Chutt... - Settings Page
 * Tabs: Marques, Concurrents, Objectifs, Sources de donnees, Forfaits
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { monitoringAPI, objectivesAPI, missionsAPI, agencyAPI } from '../services/api';
import BrandWizard from '../components/wizard/BrandWizard';
import MissionCard from '../components/shared/MissionCard';
import GlassCard from '../components/shared/GlassCard';
import ScrollReveal from '../components/shared/ScrollReveal';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import { PageTransition } from '../components/shared';
import TeamTab from '../components/settings/TeamTab';
import { colors } from '../styles/colors';

// Sectors list (extended)
const SECTORS = [
  { id: 'telecom', name: 'Telecommunications' },
  { id: 'banque_finance', name: 'Banque & Finance' },
  { id: 'tech', name: 'Technologie' },
  { id: 'mines_energie', name: 'Mines & Energie' },
  { id: 'agriculture', name: 'Agriculture & Agroalimentaire' },
  { id: 'hospitality', name: 'Hotellerie, Tourisme & Luxe' },
  { id: 'distribution', name: 'Distribution & Retail' },
  { id: 'btp', name: 'BTP & Immobilier' },
  { id: 'transport', name: 'Transport & Logistique' },
  { id: 'sante', name: 'Sante & Pharma' },
  { id: 'media', name: 'Medias & Communication' },
  { id: 'sport', name: 'Sport & Divertissement' },
  { id: 'mode', name: 'Mode & Beaute' },
  { id: 'education', name: 'Education' },
  { id: 'politique', name: 'Politique, Droit & ONG' },
  { id: 'autre', name: 'Autre' },
];

// Profile types
const PROFILE_TYPES = [
  { id: 'personnalite_publique', name: 'Personnalite Publique', description: 'Anticiper les crises, proteger votre image, gerer les contre-narratifs.' },
  { id: 'artiste', name: 'Artiste / Musicien', description: 'Proteger votre image artistique, gerer la notoriete, suivre le buzz.' },
  { id: 'createur', name: 'Createur / Influenceur', description: 'Engagement communaute, croissance audience, buzz positif.' },
  { id: 'organisation', name: 'Organisation / Entreprise', description: 'KPIs business, satisfaction client, veille concurrentielle.' },
  { id: 'agence_rp', name: 'Agence RP', description: 'Vue multi-marques, rapports clients, gestion portefeuille.' },
];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function SettingsPage() {
  const { selectedBrand, setSelectedBrand, brands, refreshBrands } = useOutletContext();
  const [searchParams] = useSearchParams();

  // Tab state — default to AGENCE (5), but respect ?tab= query param
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam !== null ? parseInt(tabParam, 10) : 5;
  });

  // Apply ?brand= query param on mount
  useEffect(() => {
    const brandParam = searchParams.get('brand');
    if (brandParam && setSelectedBrand) {
      setSelectedBrand(parseInt(brandParam, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [brandList, setBrandList] = useState(brands || []);

  // Creation progress state
  const [creationProgress] = useState(null);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);

  // Edit brand dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandForm, setBrandForm] = useState({
    name: '',
    sector: '',
    profile_type: 'organisation',
    keywords: '',
    client_email: '',
  });

  // Objectives state
  const [objectives, setObjectives] = useState([]);
  const [loadingObjectives, setLoadingObjectives] = useState(false);

  // Missions state
  const [missions, setMissions] = useState([]);
  const [missionsLoading, setMissionsLoading] = useState(false);

  // Apify / Sources state
  const [apifyPlatforms, setApifyPlatforms] = useState([]);
  const [apifyAvailable, setApifyAvailable] = useState(false);
  const [collectResults, setCollectResults] = useState(null);


  // Agency / Team state (currentUserRole needed for FORFAITS tab admin check)
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [agencyLogoUrl, setAgencyLogoUrl] = useState('');
  const [agencyAccentColor, setAgencyAccentColor] = useState(colors.primary);
  const [agencySenderEmail, setAgencySenderEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmailLoading, setTestEmailLoading] = useState(false);

  // Brand plan state (FORFAITS tab)
  const [brandPlans, setBrandPlans] = useState([]);
  const [brandPlansLoading, setBrandPlansLoading] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planDialogBrand, setPlanDialogBrand] = useState(null);
  const [planForm, setPlanForm] = useState({
    tier: 'pro',
    missions: ['protect', 'visibility', 'trust'],
    pages: ['radar', 'comprendre', 'agir', 'mesurer'],
    report_frequency: 'hebdomadaire',
    price_monthly: 100,
    has_targeted_objectives: false,
    discount_pct: 0,
  });
  const [savingPlan, setSavingPlan] = useState(false);

  // Brand URLs state (social + digital properties)
  const [brandUrls, setBrandUrls] = useState({
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    linkedin_url: '',
    youtube_url: '',
    tiktok_url: '',
    website_url: '',
    shop_url: '',
    google_maps_url: '',
    tripadvisor_url: '',
    booking_url: '',
    marketplace_url: '',
  });
  const [savingUrls, setSavingUrls] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Competitors state (benchmark)
  const [competitors, setCompetitors] = useState([]);
  const [newCompetitor, setNewCompetitor] = useState('');

  // Update brand list when brands prop changes
  useEffect(() => {
    setBrandList(brands || []);
  }, [brands]);

  // Fetch Apify platforms on mount
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const res = await monitoringAPI.getApifyPlatforms();
        setApifyPlatforms(res.data?.platforms || []);
        setApifyAvailable(res.data?.available || false);
      } catch (err) {
        console.error('Apify platforms fetch error:', err);
      }
    };
    fetchPlatforms();
  }, []);

  // Fetch objectives when brand changes (no spinner if data already loaded)
  useEffect(() => {
    const fetchObjectives = async () => {
      if (!selectedBrand) {
        setObjectives([]);
        return;
      }

      const showSpinner = objectives.length === 0;
      if (showSpinner) setLoadingObjectives(true);
      try {
        const res = await objectivesAPI.getProgress(selectedBrand);
        setObjectives(res.data?.objectives || []);
      } catch (err) {
        console.error('Objectives fetch error:', err);
        if (objectives.length === 0) setObjectives([]);
      } finally {
        if (showSpinner) setLoadingObjectives(false);
      }
    };

    fetchObjectives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  // Fetch missions when brand changes
  const fetchMissions = useCallback(async () => {
    if (!selectedBrand) return;
    // No spinner if missions already loaded (cache will serve fast)
    const showSpinner = missions.length === 0;
    if (showSpinner) setMissionsLoading(true);
    try {
      const res = await missionsAPI.getProgress(selectedBrand);
      const data = res.data || res;
      setMissions(data.missions || []);
    } catch (err) {
      console.warn('Missions fetch warning:', err);
    } finally {
      if (showSpinner) setMissionsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  useEffect(() => { fetchMissions(); }, [fetchMissions]);

  // Load social URLs when brand changes
  useEffect(() => {
    if (selectedBrand && brands) {
      const brand = brands.find(b => b.id === selectedBrand);
      const urls = brand?.social_urls || {};
      setBrandUrls({
        facebook_url: urls.facebook_url || '',
        instagram_url: urls.instagram_url || '',
        twitter_url: urls.twitter_url || '',
        linkedin_url: urls.linkedin_url || '',
        youtube_url: urls.youtube_url || '',
        tiktok_url: urls.tiktok_url || '',
        website_url: urls.website_url || '',
        shop_url: urls.shop_url || '',
        google_maps_url: urls.google_maps_url || '',
        tripadvisor_url: urls.tripadvisor_url || '',
        booking_url: urls.booking_url || '',
        marketplace_url: urls.marketplace_url || '',
      });
    }
  }, [selectedBrand, brands]);

  // Save social URLs to backend
  const handleSaveUrls = async () => {
    if (!selectedBrand) return;
    setSavingUrls(true);
    try {
      await monitoringAPI.updateSocialUrls(selectedBrand, brandUrls);
      setSuccess('URLs des reseaux sociaux sauvegardees');
      if (refreshBrands) await refreshBrands();
    } catch (err) {
      console.error('Save URLs error:', err);
      setError('Erreur lors de la sauvegarde des URLs');
    } finally {
      setSavingUrls(false);
    }
  };

  // Fetch competitors for selected brand
  const fetchCompetitors = useCallback(async () => {
    if (!selectedBrand) return;
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const resp = await fetch(`${API}/api/benchmark/competitors/${selectedBrand}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        setCompetitors(data.competitors || []);
      }
    } catch (e) { /* silent */ }
  }, [selectedBrand]);

  useEffect(() => { fetchCompetitors(); }, [fetchCompetitors]);

  // Handle brand form change
  const handleBrandFormChange = (field, value) => {
    setBrandForm((prev) => ({ ...prev, [field]: value }));
  };

  // Open edit brand dialog
  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setBrandForm({
      name: brand.name,
      sector: brand.sector || '',
      profile_type: brand.profile_type || 'organisation',
      keywords: brand.keywords?.join(', ') || '',
      client_email: brand.client_email || '',
    });
    setEditDialogOpen(true);
  };

  // Save edited brand (keywords locally + profile via API)
  // If name or keywords changed, auto-refresh images + cleanup stale mentions
  const handleSaveBrand = async () => {
    if (!brandForm.name.trim()) {
      setError('Le nom de la marque est requis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newName = brandForm.name.trim();
      const newKeywords = brandForm.keywords
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k);

      // Detect if name or keywords changed (triggers re-scrape)
      const oldName = editingBrand.name || '';
      const oldKeywords = (editingBrand.keywords || []).join(',');
      const nameChanged = newName !== oldName;

      // If name changed, auto-update the first keyword (which is usually the brand name)
      if (nameChanged && newKeywords.length > 0) {
        const oldFirstKw = (editingBrand.keywords || [])[0] || '';
        // Replace first keyword if it matched the old name (common pattern)
        if (oldFirstKw === oldName || oldFirstKw.toLowerCase() === oldName.toLowerCase()) {
          newKeywords[0] = newName;
        }
      }

      const keywordsChanged = newKeywords.join(',') !== oldKeywords;

      // Save name, keywords, profile_type, sector and client_email via API
      await monitoringAPI.updateBrandProfile(editingBrand.id, {
        name: newName,
        keywords: newKeywords,
        profile_type: brandForm.profile_type,
        sector: brandForm.sector,
        client_email: brandForm.client_email || null,
      });

      // Update locally (name + keywords + profile fields)
      setBrandList((prev) =>
        prev.map((b) =>
          b.id === editingBrand.id
            ? {
                ...b,
                name: newName,
                sector: brandForm.sector,
                profile_type: brandForm.profile_type,
                keywords: newKeywords,
                client_email: brandForm.client_email || null,
              }
            : b
        )
      );

      // Refresh brands from server to sync
      if (refreshBrands) await refreshBrands();

      // If name or keywords changed, re-scrape images + cleanup old mentions
      if (nameChanged || keywordsChanged) {
        setSuccess('Marque mise a jour. Actualisation des donnees en cours...');
        setEditDialogOpen(false);

        // Re-fetch brand photo with new name (force=true)
        try {
          await monitoringAPI.fetchBrandImages(editingBrand.id, true);
        } catch (e) {
          console.warn('fetchBrandImages after name change failed:', e);
        }

        // Cleanup mentions that no longer match the new keywords
        try {
          await monitoringAPI.cleanupMentions(editingBrand.id);
        } catch (e) {
          console.warn('cleanupMentions after name change failed:', e);
        }

        setSuccess('Marque mise a jour et donnees actualisees. Relancez l\'actualisation sur Radar pour collecter avec les nouveaux mots-cles.');
      } else {
        setSuccess('Marque mise a jour');
        setEditDialogOpen(false);
      }
    } catch (err) {
      console.error('Save brand error:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Delete brand
  const handleDeleteBrand = (brandId) => {
    setConfirmDialog({
      open: true,
      title: 'Supprimer',
      message: 'Etes-vous sur de vouloir supprimer cette marque et toutes ses donnees ?',
      onConfirm: async () => {
        try {
          await monitoringAPI.deleteBrand(brandId);
          setBrandList((prev) => prev.filter((b) => b.id !== brandId));
          if (refreshBrands) {
            await refreshBrands();
          }
          setSuccess('Marque supprimee');
        } catch (err) {
          console.error('Delete brand error:', err);
          setError('Erreur lors de la suppression');
        }
      },
    });
  };

  // Handle brand created from wizard (scraping is done in wizard now)
  const handleBrandCreated = async (brandId, brandName, keywords) => {
    // Immediately add to local list for instant feedback
    const newBrand = {
      id: brandId,
      name: brandName,
      keywords: keywords || [brandName],
      sector: 'autre',
    };
    setBrandList((prev) => [...prev, newBrand]);

    // Select the new brand
    if (setSelectedBrand) {
      setSelectedBrand(brandId);
    }

    // Also refresh from server to sync
    if (refreshBrands) {
      await refreshBrands();
    }

    setSuccess(`Marque "${brandName}" creee avec succes.`);
  };


  // Collect from a single Apify platform
  // handleCollectPlatform removed — collect is triggered from Radar/Actualiser only

  // Platform icon colors
  const PLATFORM_COLORS = {
    facebook: '#1877F2',
    instagram: '#E4405F',
    twitter: '#1DA1F2',
    linkedin: '#0A66C2',
    youtube: '#FF0000',
    tiktok: '#000000',
    google_maps: '#4285F4',
  };

  // Fetch agency info to get currentUserRole + targeted objectives flag
  const fetchAgencyData = useCallback(async () => {
    try {
      const meRes = await agencyAPI.getMe();
      setCurrentUserRole(meRes.data?.role || null);
      setAgencyLogoUrl(meRes.data?.logo_url || '');
      setAgencyAccentColor(meRes.data?.accent_color || colors.primary);
      setAgencySenderEmail(meRes.data?.report_sender_email || '');
      // Fetch email status for admin
      if (meRes.data?.role === 'admin_agence') {
        try {
          const emailRes = await agencyAPI.getEmailStatus();
          setEmailStatus(emailRes.data);
        } catch (e) {
          console.warn('Email status fetch error:', e);
        }
      }
    } catch (err) {
      console.error('Agency data fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchAgencyData();
  }, [fetchAgencyData]);

  // Auto-calculate price from plan options
  const calculatePrice = (form) => {
    const PRICING = { base: 500, per_mission: 200, page_comprendre: 150, page_agir: 150, freq_hebdo: 300, freq_quotidien: 800, objectifs_etendus: 300 };
    let price = PRICING.base;
    price += Math.max(0, (form.missions?.length || 1) - 1) * PRICING.per_mission;
    if (form.pages?.includes('comprendre')) price += PRICING.page_comprendre;
    if (form.pages?.includes('agir')) price += PRICING.page_agir;
    if (form.report_frequency === 'hebdomadaire') price += PRICING.freq_hebdo;
    if (form.report_frequency === 'quotidien') price += PRICING.freq_quotidien;
    if (form.has_targeted_objectives) price += PRICING.objectifs_etendus;
    return price;
  };

  // Fetch brand plans when FORFAITS tab is active
  const fetchBrandPlans = useCallback(async () => {
    // Only show loading spinner if we don't already have data
    const showSpinner = brandPlans.length === 0;
    if (showSpinner) setBrandPlansLoading(true);
    try {
      const res = await agencyAPI.getBrandPlans();
      setBrandPlans(res.data || []);
    } catch (err) {
      console.error('Brand plans fetch error:', err);
      if (brandPlans.length === 0) setBrandPlans([]);
    } finally {
      if (showSpinner) setBrandPlansLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((activeTab === 4 || activeTab === 6) && currentUserRole === 'admin_agence' && brandPlans.length === 0) {
      fetchBrandPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentUserRole]);

  // Open plan configuration dialog — missions come from API (source of truth via backend)
  const handleOpenPlanDialog = (plan) => {
    setPlanDialogBrand(plan);
    const form = {
      tier: plan.tier || 'pro',
      missions: plan.missions || [],
      pages: plan.pages || ['radar', 'comprendre', 'agir', 'mesurer'],
      report_frequency: plan.report_frequency || 'hebdomadaire',
      has_targeted_objectives: plan.has_targeted_objectives || false,
      discount_pct: plan.discount_pct || 0,
    };
    form.price_monthly = calculatePrice(form);
    setPlanForm(form);
    setPlanDialogOpen(true);
  };

  // Save brand plan
  const handleSaveBrandPlan = async () => {
    if (!planDialogBrand) return;
    setSavingPlan(true);
    try {
      await agencyAPI.setBrandPlan(planDialogBrand.brand_id, planForm);
      setSuccess('Forfait mis a jour');
      setPlanDialogOpen(false);
      fetchBrandPlans();
    } catch (err) {
      console.error('Save brand plan error:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde du forfait');
    } finally {
      setSavingPlan(false);
    }
  };


  // Page labels
  const PAGE_LABELS = {
    radar: 'Radar',
    comprendre: 'Comprendre',
    agir: 'Agir',
    mesurer: 'Mesurer',
  };

  // Tier labels + colors for FORFAITS tab
  const TIER_CONFIG = {
    essentiel: { label: 'Essentiel', color: colors.textMed, bg: `${colors.textLight}18` },
    pro: { label: 'Pro', color: colors.primary, bg: `${colors.primary}14` },
    premium: { label: 'Premium', color: colors.priorityHigh, bg: `${colors.priorityHigh}14` },
  };

  // Mission labels
  const MISSION_LABELS = {
    protect: 'Proteger',
    visibility: 'Rayonner',
    trust: 'Confiance',
    community: 'Federer',
  };

  // Frequency labels
  const FREQ_LABELS = {
    quotidien: 'Quotidien',
    hebdomadaire: 'Hebdomadaire',
    mensuel: 'Mensuel',
  };

  const isAdmin = currentUserRole === 'admin_agence';

  // Get sector label
  const getSectorLabel = (sectorId) => {
    return SECTORS.find((s) => s.id === sectorId)?.name || sectorId || 'Non defini';
  };

  // Get profile type label
  const getProfileTypeLabel = (typeId) => {
    return PROFILE_TYPES.find((p) => p.id === typeId)?.name || typeId || '';
  };

  // Get selected brand info
  const selectedBrandInfo = brandList.find((b) => b.id === selectedBrand);

  return (
    <PageTransition>
    <Box sx={{ overflowX: 'hidden', bgcolor: colors.ivory, minHeight: '80vh' }}>
      {/* Header — aligned with Radar / Comprendre / Agir / Mesurer */}
      <ScrollReveal>
        <Box sx={{ pt: 5, pb: 4, mb: 4 }}>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: { xs: '3.2rem', md: '4.8rem' },
              color: colors.charcoal,
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            Vos{' '}
            <Box component="em" sx={{ color: colors.primary, fontStyle: 'italic' }}>
              parametres
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '1rem',
              color: colors.textMed,
              fontWeight: 400,
            }}
          >
            Gerez vos marques, objectifs et sources de donnees
          </Typography>
          <Box sx={{ mb: 6 }} />
        </Box>
      </ScrollReveal>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs — editorial luxe */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mb: 4, flexWrap: 'wrap', rowGap: 1 }}>
        {[
          { label: 'AGENCE', idx: 5 },
          ...(isAdmin ? [{ label: 'EQUIPE', idx: 6 }] : []),
          { label: 'MARQUES', idx: 0 },
          { label: 'CONCURRENTS', idx: 1 },
          { label: 'OBJECTIFS', idx: 2 },
          { label: 'SOURCES DE DONNEES', idx: 3 },
          { label: 'FORFAITS', idx: 4 },

        ].map((tab, i) => (
          <React.Fragment key={tab.idx}>
            {i > 0 && (
              <Typography sx={{ color: colors.textLight, mx: 2.5, fontSize: '0.8rem', userSelect: 'none' }}>
                ·
              </Typography>
            )}
            <Box
              onClick={() => setActiveTab(tab.idx)}
              sx={{
                cursor: 'pointer',
                pb: 1,
                borderBottom: activeTab === tab.idx ? `2px solid ${colors.primary}` : '2px solid transparent',
                transition: 'border-color 0.2s ease',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: activeTab === tab.idx ? 600 : 400,
                  letterSpacing: '0.12em',
                  color: activeTab === tab.idx ? colors.charcoal : colors.textLight,
                  transition: 'color 0.2s ease',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: colors.charcoal,
                  },
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Box>

      <Box>

        {/* Tab 1: Marques */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
                Mes Marques ({brandList.length}/10)
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setWizardOpen(true)}
                disabled={brandList.length >= 10}
                sx={{
                  bgcolor: colors.primary,
                  borderRadius: '100px',
                  textTransform: 'none',
                  fontFamily: '"DM Sans", sans-serif',
                  fontWeight: 600,
                  '&:hover': { bgcolor: colors.primaryDark },
                }}
              >
                Nouvelle marque
              </Button>
            </Box>

            <GlassCard glow={false} hover={false} sx={{ p: 3 }}>
              <List sx={{ p: 0 }}>
                {brandList.map((brand) => (
                  <React.Fragment key={brand.id}>
                    <ListItem
                      onClick={() => setSelectedBrand && setSelectedBrand(brand.id)}
                      sx={{
                        bgcolor:
                          brand.id === selectedBrand ? `${colors.primary}08` : 'transparent',
                        borderRadius: '16px',
                        border:
                          brand.id === selectedBrand
                            ? `1px solid ${colors.primary}`
                            : `1px solid transparent`,
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: brand.id === selectedBrand ? `${colors.charcoal}15` : `${colors.charcoal}05`,
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <BusinessIcon sx={{ mr: 2, color: colors.charcoal }} />
                      <ListItemText
                        primary={
                          <Typography
                            fontWeight={brand.id === selectedBrand ? 'bold' : 'normal'}
                          >
                            {brand.name}
                          </Typography>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'block' }}>
                            <Typography variant="caption" color="textSecondary" component="span">
                              {getSectorLabel(brand.sector)}
                              {brand.profile_type && (
                                <span style={{ marginLeft: 8, color: colors.accent }}>
                                  {getProfileTypeLabel(brand.profile_type)}
                                </span>
                              )}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                mt: 0.5,
                                flexWrap: 'wrap',
                              }}
                            >
                              {brand.keywords?.slice(0, 3).map((kw, i) => (
                                <Chip
                                  key={i}
                                  label={kw}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              ))}
                              {brand.keywords?.length > 3 && (
                                <Chip
                                  label={`+${brand.keywords.length - 3}`}
                                  size="small"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Modifier">
                          <IconButton aria-label="Modifier la marque" size="small" onClick={() => handleEditBrand(brand)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            aria-label="Supprimer la marque"
                            size="small"
                            onClick={() => handleDeleteBrand(brand.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}

                {brandList.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">
                      Aucune marque configuree
                    </Typography>
                  </Box>
                )}
              </List>
            </GlassCard>

          </Box>
        </TabPanel>

        {/* Tab 1: Concurrents */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 3 }}>
            {!selectedBrand ? (
              <Alert severity="info">
                Selectionnez une marque dans le menu pour gerer ses concurrents.
              </Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
                      Concurrents — {brands?.find(b => b.id === selectedBrand)?.name || ''}
                    </Typography>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, mt: 0.5 }}>
                      Ajoutez 1 concurrent pour le benchmark comparatif.
                    </Typography>
                  </Box>
                </Box>

                <GlassCard glow={false} hover={false} sx={{ p: 3, mb: 3 }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', fontWeight: 600, color: colors.charcoal, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 2 }}>
                    Ajouter un concurrent
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      value={newCompetitor}
                      onChange={(e) => setNewCompetitor(e.target.value)}
                      placeholder="Nom du concurrent"
                      size="small"
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-root': {
                          borderRadius: '100px', fontSize: '0.82rem',
                          fontFamily: '"DM Sans", sans-serif',
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      disabled={!newCompetitor.trim() || competitors.length >= 1}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                          const resp = await fetch(`${API}/api/benchmark/competitors/${selectedBrand}`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              ...(token ? { Authorization: `Bearer ${token}` } : {}),
                            },
                            body: JSON.stringify({ name: newCompetitor.trim(), keywords: [] }),
                          });
                          if (resp.ok) {
                            setNewCompetitor('');
                            fetchCompetitors();
                            setSuccess('Collecte initiale en cours (90 jours)...');
                          }
                        } catch (e) { /* silent */ }
                      }}
                      sx={{
                        borderRadius: '100px', fontFamily: '"DM Sans", sans-serif',
                        fontSize: '0.78rem', bgcolor: colors.primary,
                        textTransform: 'none', px: 3,
                        '&:hover': { bgcolor: colors.primaryDark },
                      }}
                    >
                      Ajouter
                    </Button>
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: colors.textLight, fontFamily: '"DM Sans", sans-serif', mt: 1.5 }}>
                    {competitors.length}/1 concurrent configure
                  </Typography>
                </GlassCard>

                <GlassCard glow={false} hover={false} sx={{ p: 0 }}>
                  {competitors.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                      <Typography sx={{
                        fontSize: '0.85rem', color: colors.textLight,
                        fontFamily: '"DM Sans", sans-serif',
                      }}>
                        Aucun concurrent configure
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.75rem', color: colors.textLight,
                        fontFamily: '"DM Sans", sans-serif', mt: 0.5,
                      }}>
                        Les concurrents apparaitront dans Comprendre &gt; Benchmark
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {competitors.map((comp) => (
                        <ListItem key={comp.id} sx={{
                          py: 1.5, px: 3,
                          borderBottom: `1px solid ${colors.borderLight}`,
                          '&:last-child': { borderBottom: 'none' },
                        }}>
                          <ListItemText
                            primary={comp.name}
                            primaryTypographyProps={{
                              fontSize: '0.88rem', fontWeight: 600,
                              fontFamily: '"DM Sans", sans-serif', color: colors.text,
                            }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              aria-label="Supprimer le concurrent"
                              size="small"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                  await fetch(`${API}/api/benchmark/competitors/${selectedBrand}/${comp.id}`, {
                                    method: 'DELETE',
                                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                                  });
                                  fetchCompetitors();
                                } catch (e) { /* silent */ }
                              }}
                              sx={{ color: colors.textLight, '&:hover': { color: colors.danger } }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </GlassCard>
              </>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: Objectifs */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 3 }}>
            {!selectedBrand ? (
              <Alert severity="info">
                Selectionnez une marque dans le menu pour gerer ses objectifs.
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
                    Objectifs — {selectedBrandInfo?.name}
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, mt: 0.5 }}>
                    Suivez votre progression vers vos objectifs de reputation.
                    {' '}<Typography
                      component="span"
                      onClick={() => setActiveTab(4)}
                      sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.primary, cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: colors.primaryDark } }}
                    >
                      Objectifs etendus : configurable dans Forfaits
                    </Typography>
                  </Typography>
                </Box>

                {/* Missions actives */}
                <GlassCard glow={false} hover={false} sx={{ p: 3, mb: 3 }}>
                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.1rem', color: colors.charcoal, mb: 0.5 }}>
                    Missions actives
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: colors.textLight, mb: 2 }}>
                    Definies lors de la creation de la marque. Pour modifier, recreez la marque via le Wizard.
                  </Typography>
                  {missions.length > 0 ? (
                    <Grid container spacing={2}>
                      {missions.map((m) => (
                        <Grid item xs={12} sm={6} key={m.mission_key}>
                          <MissionCard
                            missionKey={m.mission_key}
                            name={m.name}
                            tagline={m.tagline}
                            score={m.score || 0}
                            icon={m.icon}
                            subObjectives={m.sub_objectives || []}
                            variant="compact"
                            loading={missionsLoading}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucune mission active. Les missions sont definies lors de la creation de la marque.
                    </Typography>
                  )}
                </GlassCard>

                {/* Objectives grouped by mission */}
                {missions.length > 0 && objectives?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {missions.map((m) => {
                      const missionObjTypes = {
                        protect: ['negative_reduction', 'serp_control', 'seo_visibility', 'response_time'],
                        visibility: ['mentions_volume', 'media_coverage', 'source_diversification'],
                        trust: ['nss_improvement', 'positive_ratio'],
                        community: ['engagement_rate', 'influencer_mentions'],
                      }[m.mission_key] || [];
                      const missionObjectives = objectives.filter(o => missionObjTypes.includes(o.objective_type || o.type));
                      if (missionObjectives.length === 0) return null;
                      return (
                        <Accordion key={m.mission_key} defaultExpanded sx={{ mb: 1.5, '&::before': { display: 'none' }, borderRadius: '16px !important', border: `1px solid ${colors.borderLight}`, bgcolor: 'rgba(255,255,255,0.65)', overflow: 'hidden' }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.textMed }} />}>
                            <Typography sx={{ fontWeight: 600, fontFamily: '"DM Sans", sans-serif', color: colors.charcoal }}>{m.name}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {missionObjectives.map((obj, idx) => (
                              <Box key={idx} sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {obj.name || obj.type_info?.name || obj.objective_type}
                                    </Typography>
                                    {obj.status && (
                                      <Chip
                                        label={
                                          { active: 'Actif', paused: 'En pause', achieved: 'Atteint', failed: 'Echoue', abandoned: 'Abandonne' }[obj.status] || obj.status
                                        }
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.65rem',
                                          fontFamily: '"DM Sans", sans-serif',
                                          fontWeight: 600,
                                          bgcolor: {
                                            active: colors.primaryBg,
                                            paused: colors.gray100,
                                            achieved: 'rgba(34,197,94,0.1)',
                                            failed: 'rgba(239,68,68,0.1)',
                                            abandoned: colors.gray100,
                                          }[obj.status] || colors.gray100,
                                          color: {
                                            active: colors.primary,
                                            paused: colors.textMed,
                                            achieved: colors.success,
                                            failed: colors.danger,
                                            abandoned: colors.textLight,
                                          }[obj.status] || colors.textMed,
                                          ...(obj.status === 'abandoned' ? { textDecoration: 'line-through' } : {}),
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Typography variant="caption" sx={{ color: colors.textMed, fontFamily: '"DM Sans", sans-serif' }}>
                                    {Math.round(obj.current_value || obj.current || 0)} / {Math.round(obj.target_value || obj.target || 0)} {obj.unit || obj.type_info?.unit || ''}
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, obj.progress_pct || obj.calculate_progress || 0)}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: colors.borderLight,
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: (obj.progress_pct || 0) >= 100 ? colors.success : (obj.progress_pct || 0) >= 70 ? colors.primary : (obj.progress_pct || 0) >= 40 ? colors.warning : colors.danger,
                                      borderRadius: 3,
                                    },
                                  }}
                                />
                              </Box>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}

                {loadingObjectives ? (
                  <LinearProgress sx={{ mb: 2 }} />
                ) : objectives.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{ p: 4, textAlign: 'center', bgcolor: colors.gray50, borderRadius: 2 }}
                  >
                    <FlagIcon sx={{ fontSize: 48, color: colors.charcoal, mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Aucun objectif defini
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Les objectifs sont crees lors de l'ajout d'une nouvelle marque.
                      <br />
                      Supprimez et recreez la marque pour definir des objectifs.
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {objectives.map((obj) => (
                      <Paper
                        key={obj.id}
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: `1px solid ${colors.borderLight}`,
                          borderRadius: '16px',
                          opacity: obj.is_achieved ? 0.7 : 1,
                          bgcolor: 'rgba(255,255,255,0.65)',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                            >
                              <Typography variant="subtitle1" fontWeight="bold">
                                {obj.name}
                              </Typography>
                              {obj.is_achieved && (
                                <CheckCircleIcon
                                  sx={{ color: colors.success, fontSize: 20 }}
                                />
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                              <Box>
                                <Typography variant="caption" color="textSecondary">
                                  Actuel
                                </Typography>
                                <Typography variant="body1" fontWeight="bold">
                                  {Math.round(obj.current)} {obj.unit}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="textSecondary">
                                  Cible
                                </Typography>
                                <Typography variant="body1" fontWeight="bold">
                                  {Math.round(obj.target)} {obj.unit}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="textSecondary">
                                  Horizon
                                </Typography>
                                <Typography variant="body1">
                                  {obj.horizon === 'weekly'
                                    ? 'Hebdo'
                                    : obj.horizon === 'monthly'
                                    ? 'Mensuel'
                                    : obj.horizon === 'quarterly'
                                    ? 'Trimestriel'
                                    : obj.horizon}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, obj.progress_pct || 0)}
                                sx={{
                                  flex: 1,
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: colors.gray200,
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor:
                                      obj.progress_pct >= 100
                                        ? colors.success
                                        : obj.progress_pct >= 50
                                        ? colors.charcoal
                                        : colors.warning,
                                    borderRadius: 4,
                                  },
                                }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{
                                  color:
                                    obj.progress_pct >= 100
                                      ? colors.success
                                      : obj.progress_pct >= 50
                                      ? colors.charcoal
                                      : colors.warning,
                                  minWidth: 50,
                                }}
                              >
                                {Math.round(obj.progress_pct || 0)}%
                              </Typography>
                            </Box>
                          </Box>

                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}

              </>
            )}
          </Box>
        </TabPanel>

        {/* Tab 3: Sources de donnees (Apify) */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ p: 3 }}>
            {!selectedBrand ? (
              <Alert severity="info">
                Selectionnez une marque dans le menu pour gerer les sources de donnees.
              </Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
                      Sources de donnees — {selectedBrandInfo?.name}
                    </Typography>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, mt: 0.5 }}>
                      Collectez des mentions depuis les reseaux sociaux via Apify.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleSaveUrls}
                    disabled={savingUrls}
                    sx={{ bgcolor: colors.primary, borderRadius: '100px', textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, flexShrink: 0, '&:hover': { bgcolor: colors.primaryDark } }}
                  >
                    {savingUrls ? 'Sauvegarde...' : 'Sauvegarder les URLs'}
                  </Button>
                </Box>

                {/* Collection results */}
                {collectResults && (
                  <Alert
                    severity={collectResults.total_saved > 0 ? 'success' : 'info'}
                    sx={{ mb: 3 }}
                    onClose={() => setCollectResults(null)}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Derniere collecte: {collectResults.total_saved} nouvelles mentions sauvegardees
                      ({collectResults.total_skipped} doublons ignores)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      {Object.entries(collectResults.platforms || {}).map(([key, data]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${data.saved || 0}/${data.collected || 0}`}
                          size="small"
                          sx={{
                            bgcolor: data.status === 'success' ? `${colors.success}20` : `${colors.danger}20`,
                            color: data.status === 'success' ? colors.success : colors.danger,
                          }}
                        />
                      ))}
                    </Box>
                  </Alert>
                )}

                {/* Brand URLs form */}
                <Paper elevation={0} sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: '16px', p: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.65)' }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: colors.charcoal, mb: 0.5 }}>
                    Profils reseaux sociaux
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textMed, mb: 2 }}>
                    Renseignez les URLs pour activer la collecte sur ces plateformes.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Page Facebook"
                        placeholder="https://www.facebook.com/mamarque"
                        value={brandUrls.facebook_url}
                        onChange={(e) => setBrandUrls((prev) => ({ ...prev, facebook_url: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Profil Instagram"
                        placeholder="https://www.instagram.com/mamarque"
                        value={brandUrls.instagram_url}
                        onChange={(e) => setBrandUrls((prev) => ({ ...prev, instagram_url: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Profil Twitter / X"
                        placeholder="https://x.com/mamarque"
                        value={brandUrls.twitter_url}
                        onChange={(e) => setBrandUrls((prev) => ({ ...prev, twitter_url: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Profil LinkedIn"
                        placeholder="https://www.linkedin.com/in/nom-prenom"
                        value={brandUrls.linkedin_url}
                        onChange={(e) => setBrandUrls((prev) => ({ ...prev, linkedin_url: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Chaine YouTube"
                        placeholder="https://www.youtube.com/@mamarque"
                        value={brandUrls.youtube_url}
                        onChange={(e) => setBrandUrls((prev) => ({ ...prev, youtube_url: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Profil TikTok"
                        placeholder="https://www.tiktok.com/@mamarque"
                        value={brandUrls.tiktok_url}
                        onChange={(e) => setBrandUrls((prev) => ({ ...prev, tiktok_url: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  {/* Digital Properties — conditional by profile+sector */}
                  {(() => {
                    const brand = brands?.find(b => b.id === selectedBrand);
                    const pt = brand?.profile_type || 'organisation';
                    const sc = brand?.sector || 'autre';
                    const EXTRA_URL_FIELDS = [
                      { key: 'website_url', label: 'Site web', placeholder: 'https://www.monsite.com', showWhen: () => true },
                      { key: 'shop_url', label: 'Boutique en ligne', placeholder: 'https://shop.example.com', showWhen: (p, s) => ['artiste', 'createur'].includes(p) || ['mode', 'distribution'].includes(s) },
                      { key: 'google_maps_url', label: 'Fiche Google Maps', placeholder: 'https://maps.google.com/...', showWhen: (p, s) => p === 'organisation' || ['hospitality', 'distribution', 'sante'].includes(s) },
                      { key: 'tripadvisor_url', label: 'TripAdvisor', placeholder: 'https://www.tripadvisor.com/...', showWhen: (_, s) => s === 'hospitality' },
                      { key: 'booking_url', label: 'Booking.com', placeholder: 'https://www.booking.com/hotel/...', showWhen: (_, s) => s === 'hospitality' },
                      { key: 'marketplace_url', label: 'Marketplace (Amazon, Jumia, Etsy...)', placeholder: 'https://www.etsy.com/shop/...', showWhen: (p, s) => ['artiste', 'createur'].includes(p) || ['mode', 'distribution'].includes(s) },
                    ];
                    const visible = EXTRA_URL_FIELDS.filter(f => f.showWhen(pt, sc));
                    if (visible.length === 0) return null;
                    return (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: colors.charcoal, mb: 0.5 }}>
                          Propriétés digitales
                        </Typography>
                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textMed, mb: 2 }}>
                          Renseignez vos propriétés web pour que l'IA ne recommande pas de créer ce qui existe déjà.
                        </Typography>
                        <Grid container spacing={2}>
                          {visible.map(f => (
                            <Grid item xs={12} sm={6} key={f.key}>
                              <TextField
                                fullWidth
                                size="small"
                                label={f.label}
                                placeholder={f.placeholder}
                                value={brandUrls[f.key]}
                                onChange={(e) => setBrandUrls((prev) => ({ ...prev, [f.key]: e.target.value }))}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    );
                  })()}
                </Paper>

                {/* Platform errors detail */}
                {collectResults && Object.entries(collectResults.platforms || {}).map(([key, data]) => (
                  data.status === 'error' && data.error && (
                    <Alert key={key} severity="error" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{key}</Typography>
                      <Typography variant="caption">{data.error}</Typography>
                    </Alert>
                  )
                ))}

                {/* Platforms list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {apifyPlatforms.map((platform) => (
                    <Paper
                      key={platform.key}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: `1px solid ${colors.borderLight}`,
                        borderRadius: '16px',
                        bgcolor: 'rgba(255,255,255,0.65)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: `${PLATFORM_COLORS[platform.platform] || colors.charcoal}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              color: PLATFORM_COLORS[platform.platform] || colors.charcoal,
                            }}
                          >
                            {platform.platform?.substring(0, 2).toUpperCase()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {platform.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {platform.platform} - {platform.actor_id}
                          </Typography>
                        </Box>
                      </Box>

                    </Paper>
                  ))}

                  {apifyPlatforms.length === 0 && apifyAvailable && (
                    <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                      Chargement des plateformes...
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </TabPanel>

        {/* ==================== TAB 4: FORFAITS ==================== */}
        <TabPanel value={activeTab} index={4}>
          <Box sx={{ p: 3 }}>
            {!isAdmin ? (
              <Alert severity="info">Seul un administrateur peut gerer les forfaits.</Alert>
            ) : brandPlansLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: colors.primary }} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
                      Forfaits par marque
                    </Typography>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, mt: 0.5 }}>
                      Configurez le niveau de service pour chaque marque cliente
                    </Typography>
                  </Box>
                </Box>

                <GlassCard glow={false} hover={false} sx={{ p: 0 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Marque</TableCell>
                          <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Forfait</TableCell>
                          <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Missions</TableCell>
                          <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Pages</TableCell>
                          <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Rapports</TableCell>
                          <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Prix/mois</TableCell>
                          <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Obj. etendus</TableCell>
                          <TableCell align="right" sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: colors.textMed }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {brandPlans.length > 0 ? brandPlans.map((plan) => {
                          const tierCfg = TIER_CONFIG[plan.tier] || TIER_CONFIG.pro;
                          return (
                            <TableRow key={plan.brand_id} hover>
                              <TableCell>
                                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: colors.charcoal }}>
                                  {plan.brand_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={tierCfg.label}
                                  size="small"
                                  sx={{ fontSize: '0.68rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, bgcolor: tierCfg.bg, color: tierCfg.color, borderRadius: '9999px', height: 22 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {(plan.missions || []).map((m) => (
                                    <Chip key={m} label={MISSION_LABELS[m] || m} size="small" sx={{ fontSize: '0.65rem', fontFamily: '"DM Sans", sans-serif', height: 20, bgcolor: `${colors.primary}08`, color: colors.primary }} />
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textMed }}>
                                  {(plan.pages || []).map((p) => PAGE_LABELS[p] || p).join(', ')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textMed }}>
                                  {FREQ_LABELS[plan.report_frequency] || plan.report_frequency}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {(plan.discount_pct || 0) > 0 ? (
                                  <Box>
                                    <Typography component="span" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textLight, textDecoration: 'line-through', mr: 0.5 }}>
                                      {plan.price_monthly}
                                    </Typography>
                                    <Typography component="span" sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: colors.charcoal }}>
                                      {Math.round(plan.price_monthly * (100 - plan.discount_pct) / 100)} euros/mois
                                    </Typography>
                                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.68rem', color: colors.textMed }}>
                                      (-{plan.discount_pct}%)
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: colors.charcoal }}>
                                    {plan.price_monthly} euros/mois
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {plan.has_targeted_objectives ? (
                                  <Chip label="Actif" size="small" sx={{ fontSize: '0.65rem', fontFamily: '"DM Sans", sans-serif', height: 20, bgcolor: 'rgba(34,197,94,0.1)', color: colors.success }} />
                                ) : (
                                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: colors.textLight }}>—</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenPlanDialog(plan)}
                                  sx={{ borderRadius: '100px', textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.75rem', borderColor: colors.primary, color: colors.primary, '&:hover': { borderColor: colors.primaryDark, bgcolor: `${colors.primary}08` } }}
                                >
                                  Configurer
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        }) : (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textLight }}>
                                Aucune marque dans l'agence
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </GlassCard>
              </>
            )}
          </Box>
        </TabPanel>

        {/* ==================== TAB 5: AGENCE (Co-branding) ==================== */}
        <TabPanel value={activeTab} index={5}>
          <Box sx={{ p: 3 }}>
            {!isAdmin ? (
              <Alert severity="info">Seul un administrateur peut modifier les parametres de l'agence.</Alert>
            ) : (
              <>
                {/* --- Configuration Email --- */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
                    Configuration Email
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, mt: 0.5 }}>
                    Statut de la configuration d'envoi des emails (rapports, alertes)
                  </Typography>
                </Box>

                <GlassCard glow={false} hover={false}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                    {emailStatus ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Chip
                            icon={emailStatus.configured ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                            label={emailStatus.configured ? 'Configure' : 'Non configure'}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.68rem',
                              fontWeight: 600,
                              fontFamily: '"DM Sans", sans-serif',
                              borderRadius: '100px',
                              bgcolor: emailStatus.configured ? '#1B5E4020' : '#B91C1C20',
                              color: emailStatus.configured ? '#1B5E40' : '#B91C1C',
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                          {emailStatus.provider && (
                            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem', color: colors.textMed }}>
                              Fournisseur : <strong>{emailStatus.provider === 'sendgrid' ? 'SendGrid' : 'Resend'}</strong>
                            </Typography>
                          )}
                        </Box>

                        {!emailStatus.configured && (
                          <Alert severity="warning" sx={{ borderRadius: '12px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem' }}>
                            Contactez votre administrateur pour configurer les cles API (SENDGRID_API_KEY ou RESEND_API_KEY).
                          </Alert>
                        )}

                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textMed }}>
                          {emailStatus.brands_with_client_email}/{emailStatus.brands_total} marques ont un email client configure
                        </Typography>

                        {emailStatus.configured && (
                          <Box>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={testEmailLoading ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 14 }} />}
                              disabled={testEmailLoading}
                              onClick={async () => {
                                setTestEmailLoading(true);
                                try {
                                  const res = await agencyAPI.testEmail();
                                  setSuccess(`Email test envoye a ${res.data?.sent_to || 'votre adresse'}`);
                                } catch (err) {
                                  setError(err.response?.data?.detail || 'Echec de l\'envoi du test');
                                } finally {
                                  setTestEmailLoading(false);
                                }
                              }}
                              sx={{
                                borderRadius: '100px',
                                textTransform: 'none',
                                fontFamily: '"DM Sans", sans-serif',
                                fontWeight: 600,
                                fontSize: '0.82rem',
                                borderColor: colors.primary,
                                color: colors.primary,
                                '&:hover': { borderColor: colors.primary, bgcolor: `${colors.primary}08` },
                              }}
                            >
                              Envoyer un email test
                            </Button>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textLight }}>
                        Chargement du statut email...
                      </Typography>
                    )}
                  </Box>
                </GlassCard>

                {/* --- Personnalisation agence --- */}
                <Box sx={{ mb: 3, mt: 4 }}>
                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, fontSize: '1.3rem', color: colors.charcoal }}>
                    Personnalisation de l'agence
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, mt: 0.5 }}>
                    Co-branding : logo, couleur accent et identite visuelle
                  </Typography>
                </Box>

                <GlassCard glow={false} hover={false}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                    <TextField
                      fullWidth
                      label="Email d'envoi des rapports"
                      placeholder="rapports@votre-agence.com"
                      helperText="Adresse email qui apparaitra comme expediteur des rapports envoyes aux clients"
                      type="email"
                      value={agencySenderEmail}
                      onChange={(e) => setAgencySenderEmail(e.target.value)}
                      onBlur={(e) => {
                        agencyAPI.updateMe({ report_sender_email: e.target.value.trim() });
                        setSuccess('Email d\'envoi mis a jour');
                      }}
                    />

                    <TextField
                      fullWidth
                      label="URL du logo agence"
                      placeholder="https://storage.supabase.co/..."
                      helperText="Logo affiche dans la navigation et les rapports PDF"
                      value={agencyLogoUrl}
                      onChange={(e) => setAgencyLogoUrl(e.target.value)}
                      onBlur={(e) => {
                        agencyAPI.updateMe({ logo_url: e.target.value.trim() });
                        setSuccess('Logo mis a jour');
                      }}
                    />

                    <Box>
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: colors.charcoal, mb: 1 }}>
                        Couleur accent
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={agencyAccentColor}
                          style={{ width: 48, height: 48, border: 'none', borderRadius: 12, cursor: 'pointer' }}
                          onChange={(e) => {
                            setAgencyAccentColor(e.target.value);
                            agencyAPI.updateMe({ accent_color: e.target.value });
                          }}
                        />
                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem', color: colors.textLight }}>
                          Couleur utilisee dans les rapports PDF et l'interface (header, footer)
                        </Typography>
                      </Box>
                    </Box>

                  </Box>
                </GlassCard>
              </>
            )}
          </Box>
        </TabPanel>

        {/* ==================== TAB 6: EQUIPE (Team management) ==================== */}
        <TabPanel value={activeTab} index={6}>
          {!isAdmin ? (
            <Alert severity="info">Seul un administrateur peut gerer l'equipe.</Alert>
          ) : (
            <TeamTab brands={brandList} brandPlans={brandPlans} />
          )}
        </TabPanel>

      </Box>

      {/* Brand Plan Configuration Dialog */}
      <Dialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, color: colors.charcoal }}>
          Configurer le forfait {planDialogBrand?.brand_name ? `— ${planDialogBrand.brand_name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Missions (read-only — defined in Wizard) */}
            <Box>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: colors.charcoal, mb: 0.5 }}>
                Missions
              </Typography>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textLight, mb: 1 }}>
                Definies lors de la creation (Wizard)
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {planForm.missions.map((m) => (
                  <Chip key={m} label={MISSION_LABELS[m] || m} size="small" sx={{ fontSize: '0.72rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, height: 24, bgcolor: `${colors.primary}14`, color: colors.primary }} />
                ))}
              </Box>
            </Box>

            {/* Pages checkboxes */}
            <Box>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: colors.charcoal, mb: 1 }}>
                Pages accessibles
              </Typography>
              <FormGroup row>
                {Object.entries(PAGE_LABELS).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={planForm.pages.includes(key)}
                        onChange={(e) => {
                          const newPages = e.target.checked
                            ? [...planForm.pages, key]
                            : planForm.pages.filter((p) => p !== key);
                          const updated = { ...planForm, pages: newPages };
                          updated.price_monthly = calculatePrice(updated);
                          setPlanForm(updated);
                        }}
                        size="small"
                        sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }}
                      />
                    }
                    label={<Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem' }}>{label}</Typography>}
                  />
                ))}
              </FormGroup>
            </Box>

            {/* Frequency dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel>Frequence des rapports</InputLabel>
              <Select
                value={planForm.report_frequency}
                label="Frequence des rapports"
                onChange={(e) => {
                  const updated = { ...planForm, report_frequency: e.target.value };
                  updated.price_monthly = calculatePrice(updated);
                  setPlanForm(updated);
                }}
              >
                {Object.entries(FREQ_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Objectifs etendus (read-only — defined in Wizard) */}
            <FormControlLabel
              control={
                <Switch
                  checked={planForm.has_targeted_objectives}
                  disabled
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.primary },
                  }}
                />
              }
              label="Objectifs etendus"
              sx={{ mt: 2, '& .MuiFormControlLabel-label': { fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', fontWeight: 600 } }}
            />
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textLight, ml: 7 }}>
              Defini lors de la creation (Wizard)
            </Typography>

            <Divider sx={{ my: 1 }} />

            {/* Calculated price (read-only) */}
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Prix calcule (euros/mois)"
              value={planForm.price_monthly}
              InputProps={{ readOnly: true }}
              inputProps={{ min: 0 }}
              sx={{ '& .MuiInputBase-input': { fontFamily: '"DM Sans", sans-serif', fontWeight: 600, color: colors.charcoal } }}
            />

            {/* Discount */}
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Remise (%)"
              value={planForm.discount_pct}
              onChange={(e) => {
                const val = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                setPlanForm({ ...planForm, discount_pct: val });
              }}
              inputProps={{ min: 0, max: 100 }}
              sx={{ '& .MuiInputBase-input': { fontFamily: '"DM Sans", sans-serif' } }}
            />

            {/* Computed final price */}
            <Box sx={{ mt: 1 }}>
              {planForm.discount_pct > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '0.95rem', fontWeight: 500, color: colors.textLight, textDecoration: 'line-through' }}>
                    {planForm.price_monthly} euros
                  </Typography>
                  <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.1rem', fontWeight: 600, color: colors.primary }}>
                    Prix final : {Math.round(planForm.price_monthly * (100 - planForm.discount_pct) / 100)} euros/mois
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: colors.textMed }}>
                    (-{planForm.discount_pct}%)
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.1rem', fontWeight: 600, color: colors.primary }}>
                  Prix final : {planForm.price_monthly} euros/mois
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPlanDialogOpen(false)}
            sx={{ borderRadius: '100px', textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, color: colors.textMed }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveBrandPlan}
            disabled={savingPlan || planForm.missions.length === 0 || planForm.pages.length === 0}
            sx={{
              bgcolor: colors.primary,
              borderRadius: '100px',
              textTransform: 'none',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              '&:hover': { bgcolor: colors.primaryDark },
            }}
          >
            {savingPlan ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Creation Progress Overlay */}
      <Dialog
        open={creationProgress !== null}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 4,
            minWidth: 400,
            textAlign: 'center',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {/* Animated spinner */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              border: `4px solid ${colors.charcoal}20`,
              borderTopColor: colors.charcoal,
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 1 }}>
              {creationProgress?.message}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {creationProgress?.detail}
            </Typography>
          </Box>

          {/* Progress bar */}
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Etape {creationProgress?.step} / {creationProgress?.total}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {Math.round((creationProgress?.step / creationProgress?.total) * 100)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(creationProgress?.step / creationProgress?.total) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: colors.gray200,
                '& .MuiLinearProgress-bar': {
                  bgcolor: colors.charcoal,
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          {/* Steps list */}
          <Box sx={{ width: '100%', textAlign: 'left' }}>
            {[
              'Creation de la marque',
              'Recherche flux RSS',
              'Analyse Google News',
              'Reseaux sociaux',
              'Finalisation'
            ].map((step, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  opacity: (creationProgress?.step || 0) > idx ? 1 : 0.4,
                }}
              >
                {(creationProgress?.step || 0) > idx ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: colors.success }} />
                ) : (creationProgress?.step || 0) === idx + 1 ? (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      bgcolor: colors.charcoal,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>
                      {idx + 1}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: `2px solid ${colors.gray200}`,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: (creationProgress?.step || 0) > idx ? colors.success :
                           (creationProgress?.step || 0) === idx + 1 ? colors.charcoal : colors.textLight,
                    fontWeight: (creationProgress?.step || 0) === idx + 1 ? 'bold' : 'normal',
                  }}
                >
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => {
          if (confirmDialog.onConfirm) confirmDialog.onConfirm();
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
      />

      {/* Brand Wizard */}
      <BrandWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onBrandCreated={handleBrandCreated}
      />

      {/* Edit Brand Dialog - with profile_type and sector integrated */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500, color: colors.charcoal }}>
          Modifier la marque
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Nom de la marque"
              value={brandForm.name}
              onChange={(e) => handleBrandFormChange('name', e.target.value)}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Type de profil</InputLabel>
              <Select
                value={brandForm.profile_type}
                label="Type de profil"
                onChange={(e) => handleBrandFormChange('profile_type', e.target.value)}
              >
                {PROFILE_TYPES.map((pt) => (
                  <MenuItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {brandForm.profile_type && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: -1.5 }}>
                {PROFILE_TYPES.find(p => p.id === brandForm.profile_type)?.description}
              </Typography>
            )}

            <FormControl fullWidth>
              <InputLabel>Secteur</InputLabel>
              <Select
                value={brandForm.sector}
                label="Secteur"
                onChange={(e) => handleBrandFormChange('sector', e.target.value)}
              >
                {SECTORS.map((sector) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Mots-cles"
              value={brandForm.keywords}
              onChange={(e) => handleBrandFormChange('keywords', e.target.value)}
              helperText="Separes par des virgules"
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Email destinataire des rapports"
              value={brandForm.client_email}
              onChange={(e) => handleBrandFormChange('client_email', e.target.value)}
              helperText="Email du client final pour recevoir les rapports automatiques"
              type="email"
              placeholder="client@example.com"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ borderRadius: '100px', textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, color: colors.textMed }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveBrand}
            disabled={loading}
            sx={{
              bgcolor: colors.primary,
              borderRadius: '100px',
              textTransform: 'none',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              '&:hover': { bgcolor: colors.primaryDark },
            }}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageTransition>
  );
}

export default SettingsPage;
